import { useContext, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Field, FieldArray, Form, Formik, FormikProps, useFormikContext } from "formik";
import { MdOutlineDelete } from "react-icons/md";
import { IoIosArrowDown } from "react-icons/io";
import {
  FiPlus,
  FiTrash,
  FiAlertOctagon,
  FiChevronDown,
  FiChevronUp,
  FiCheck,
  FiX,
  FiEdit2,
  FiTrash2,
  FiSave,
} from "react-icons/fi";
import { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import format from "date-fns/format";
import * as Yup from "yup";
import { BsCloudArrowUp } from "react-icons/bs";
import imageCompression from "browser-image-compression";
import { enqueueSnackbar } from "notistack";
import { api } from "../../../services/api";

import { PageTitle } from "../../../components/PageTitle";
import { AuthContext } from "../../../contexts/AuthContext";
import { FormButton } from "../../../components/FormButton";
import { SelectSingle } from "../../../components/SelectSingle";
import { DateHourPicker } from "../../../components/DateHourPicker";
import { ModalAddSector } from "../components/ModalAddSector";
import { ModalAddCostumer } from "../components/ModalAddCostumer";
import { ModalAddResponsible } from "../components/ModalAddResponsible";
import { ModalAddSignatureLocal } from "../components/ModalAddSignatureLocal";
import { Modal, ModalButton } from "../../../components/Modal";
import { AddButton } from "../../../components/AddButton";
import { Loading } from "../../../components/Loading";
import "react-datepicker/dist/react-datepicker.css";
import { SelectSearch } from "../../../components/SelectSearch";
import { SelectSearchSector } from "../../../components/SelectSearchSector";
import { MultiSelectDropdown } from "../../../components/MultiSelectDropdown";

registerLocale("ptBR", ptBR);

interface SelectSectors {
  id: string;
  name: string;
}

interface SelectServices {
  id: string;
  name: string;
  value?: string;
  label?: string;
}

interface SelectEquipments {
  id: string;
  name: string;
  grade: string;
}

interface Responsible {
  id: string;
  name: string;
}

interface Technician {
  id: string;
  name: string;
}

interface FormServices {
  service_id: string;
}

interface FormEquipments {
  equipment_id: string;
  name: string;
  grade: string;
}

interface ImageFile {
  id?: string;
  file_name?: string;
  name?: string;
}

interface FormSectors {
  id?: string;
  sector_id: string;
  equipments: FormEquipments[];
  observation: string;
  images: (File | ImageFile)[];
  imagesPreview: string[];
  show: boolean;
}

interface FormTechVisitReport {
  costumer_id: string;
  user_id: string;
  begin: string;
  end: string;
  observation: string;
  pending: string;
  sectors: FormSectors[];
  services: FormServices[];
  responsible_id: string;
  technician_id: string;
  responsible_signature: string;
  technician_signature: string;
  emergency_observations?: string[];
}

interface EmergencyObservation {
  id: string;
  text: string;
  status: "open" | "closed";
  opened_at: string;
  closed_at: string | null;
  user: {
    id: string;
    name: string;
  };
}

// Interface para dados salvos no localStorage
interface SavedDraftData {
  formValues: Partial<FormTechVisitReport>;
  startDate: string;
  endDate: string;
  selectedServicesForm: string[];
  idCostumer: string;
  technicianSignature: string;
  responsibleSignature: string;
  imagesBase64: { [sectorIndex: number]: string[] };
  savedAt: number;
}

// Chave do localStorage para o rascunho
const DRAFT_STORAGE_KEY = "@LuviApp:reportDraft";
const DRAFT_IMAGES_DB_NAME = "LuviAppDraftImages";
const DRAFT_IMAGES_STORE_NAME = "images";

// Funções para IndexedDB (armazenamento de imagens com limite muito maior)
const openIndexedDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DRAFT_IMAGES_DB_NAME, 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(DRAFT_IMAGES_STORE_NAME)) {
        db.createObjectStore(DRAFT_IMAGES_STORE_NAME);
      }
    };
  });
};

const saveImagesToIndexedDB = async (imagesBase64: { [sectorIndex: number]: string[] }): Promise<void> => {
  try {
    const db = await openIndexedDB();
    const transaction = db.transaction(DRAFT_IMAGES_STORE_NAME, "readwrite");
    const store = transaction.objectStore(DRAFT_IMAGES_STORE_NAME);

    // Limpar imagens antigas
    store.clear();

    // Salvar novas imagens
    for (const [sectorIndex, images] of Object.entries(imagesBase64)) {
      store.put(images, `sector_${sectorIndex}`);
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };
    });
  } catch (error) {
    console.warn("IndexedDB não disponível, tentando localStorage como fallback");
    throw error;
  }
};

const loadImagesFromIndexedDB = async (): Promise<{ [sectorIndex: number]: string[] }> => {
  try {
    const db = await openIndexedDB();
    const transaction = db.transaction(DRAFT_IMAGES_STORE_NAME, "readonly");
    const store = transaction.objectStore(DRAFT_IMAGES_STORE_NAME);

    return new Promise((resolve, reject) => {
      const result: { [sectorIndex: number]: string[] } = {};
      const cursorRequest = store.openCursor();

      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const key = cursor.key as string;
          const sectorIndex = parseInt(key.replace("sector_", ""));
          result[sectorIndex] = cursor.value;
          cursor.continue();
        } else {
          db.close();
          resolve(result);
        }
      };

      cursorRequest.onerror = () => {
        db.close();
        reject(cursorRequest.error);
      };
    });
  } catch (error) {
    console.warn("Erro ao carregar imagens do IndexedDB");
    return {};
  }
};

const clearIndexedDB = async (): Promise<void> => {
  try {
    const db = await openIndexedDB();
    const transaction = db.transaction(DRAFT_IMAGES_STORE_NAME, "readwrite");
    const store = transaction.objectStore(DRAFT_IMAGES_STORE_NAME);
    store.clear();

    return new Promise((resolve) => {
      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
      transaction.onerror = () => {
        db.close();
        resolve(); // Não falhar se não conseguir limpar
      };
    });
  } catch {
    // Ignorar erros ao limpar
  }
};

// Funções utilitárias para o auto-save
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

const base64ToFile = async (base64: string, fileName: string): Promise<File> => {
  const response = await fetch(base64);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type });
};

const schema = Yup.object().shape({
  begin: Yup.string().required("Selecione uma data e hora"),
  end: Yup.string().required("Selecione uma data e hora"),
  costumer_id: Yup.string().required("Selecione um cliente"),
  services: Yup.array().of(Yup.string()).required("Selecione os serviços"),
  pending: Yup.string().required("Selecione se existe pendência ou não"),
  responsible_id: Yup.string().required("Selecione um responsável"),
  technician_id: Yup.string().required("Selecione um técnico"),
});

// Função para criar uma assinatura dos valores do formulário para detectar mudanças
const getFormSignature = (values: FormTechVisitReport): string => {
  // Cria uma assinatura baseada nos dados relevantes, incluindo contagem de imagens por setor
  const sectorsSignature = values.sectors.map((s, idx) => ({
    sector_id: s.sector_id,
    observation: s.observation,
    // Incluir grades dos equipamentos para detectar mudanças nas notas
    equipmentsWithGrades: s.equipments.map(eq => ({ id: eq.equipment_id, grade: eq.grade })),
    // Contar imagens que são File (não objetos vazios)
    imagesCount: s.images?.filter(img => img instanceof File || (img && (img as any).id)).length || 0,
    imagesPreviewCount: s.imagesPreview?.length || 0,
  }));

  return JSON.stringify({
    costumer_id: values.costumer_id,
    begin: values.begin,
    end: values.end,
    observation: values.observation,
    pending: values.pending,
    responsible_id: values.responsible_id,
    technician_id: values.technician_id,
    servicesCount: values.services?.length || 0,
    sectors: sectorsSignature,
  });
};

// Componente para auto-save quando os valores mudam
function AutoSaveFormik({ onSave }: { onSave: () => void }) {
  const { values } = useFormikContext<FormTechVisitReport>();
  const isFirstRender = useRef(true);
  const lastSignatureRef = useRef<string>("");

  useEffect(() => {
    // Pular o primeiro render para não salvar ao carregar
    if (isFirstRender.current) {
      isFirstRender.current = false;
      lastSignatureRef.current = getFormSignature(values);
      return;
    }

    // Verificar se houve mudança real usando assinatura
    const currentSignature = getFormSignature(values);
    if (currentSignature !== lastSignatureRef.current) {
      lastSignatureRef.current = currentSignature;
      onSave();
    }
  }, [values, onSave]);

  return null;
}

export function TechVisitReportCreateUnified() {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [report, setReport] = useState<FormTechVisitReport>();
  const [selectSectors, setSelectSectors] = useState<SelectSectors[]>([]);
  const [selectServices, setSelectServices] = useState<SelectServices[]>([]);
  const [selectEquipments, setSelectEquipments] = useState<FormEquipments[]>(
    [],
  );
  const [responsibles, setResponsibles] = useState<Responsible[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [idCostumer, setIdCostumer] = useState("");
  const [modalAddSector, setModalAddSector] = useState(false);
  const [modalAddCostumer, setModalAddCostumer] = useState(false);
  const [modalAddResponsible, setModalAddResponsible] = useState(false);
  const [modalAddSignature, setModalAddSignature] = useState(false);
  const [signatureType, setSignatureType] = useState("");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedServicesForm, setSelectedServicesForm] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estados para armazenar assinaturas localmente
  const [localSignatures, setLocalSignatures] = useState<{
    technician_signature?: string;
    responsible_signature?: string;
  }>({});

  // Estados para dados das assinaturas e confirmação
  const [technicianSignatureData, setTechnicianSignatureData] = useState("");
  const [responsibleSignatureData, setResponsibleSignatureData] = useState("");
  const [confirmDelete, setConfirmDelete] = useState({ show: false, type: "" });

  // Refs para manter os valores mais recentes das assinaturas (usados no saveDraft)
  const technicianSignatureRef = useRef("");
  const responsibleSignatureRef = useRef("");

  // Estados para preview de imagem
  const [imagePreview, setImagePreview] = useState({ show: false, src: "", alt: "" });

  // Estados para upload de múltiplas imagens
  const [uploadingImages, setUploadingImages] = useState<{ [sectorIndex: number]: boolean }>({});
  const [uploadProgress, setUploadProgress] = useState<{ [sectorIndex: number]: { current: number; total: number } }>({});
  const [dragOver, setDragOver] = useState<{ [sectorIndex: number]: boolean }>({});

  // Estados para observações emergenciais
  const [emergencyObservations, setEmergencyObservations] = useState<EmergencyObservation[]>([]);
  const [showEmergencyPanel, setShowEmergencyPanel] = useState(false);
  const [expandedObservations, setExpandedObservations] = useState<Set<string>>(new Set());
  const [selectedObservationsToClose, setSelectedObservationsToClose] = useState<Set<string>>(new Set());
  const [showEmergencyConfirmModal, setShowEmergencyConfirmModal] = useState(false);
  const [hasEmergency, setHasEmergency] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormTechVisitReport | null>(null);
  const emergencyPanelRef = useRef<HTMLDivElement>(null);

  // Estado para rastrear tempo de inatividade
  const [lastActiveTime, setLastActiveTime] = useState<number>(Date.now());

  // Estados para auto-save
  const [hasDraft, setHasDraft] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const formikRef = useRef<FormikProps<FormTechVisitReport>>(null);

  const navigate = useNavigate();

  // Verificar se o usuário está carregado
  if (!user) {
    return <Loading />;
  }

  // Manter refs sincronizados com os estados das assinaturas
  useEffect(() => {
    technicianSignatureRef.current = technicianSignatureData;
  }, [technicianSignatureData]);

  useEffect(() => {
    responsibleSignatureRef.current = responsibleSignatureData;
  }, [responsibleSignatureData]);

  // Função para salvar rascunho no localStorage
  const saveDraft = useCallback(async (
    formValues: FormTechVisitReport,
    sectors: FormSectors[]
  ) => {
    // Só salvar se for novo relatório (não edição)
    if (id !== undefined) return;

    try {
      // Converter imagens para base64
      const imagesBase64: { [sectorIndex: number]: string[] } = {};

      for (let i = 0; i < sectors.length; i++) {
        const sector = sectors[i];
        if (sector.images && sector.images.length > 0) {
          imagesBase64[i] = [];
          for (const image of sector.images) {
            if (image instanceof File) {
              try {
                const base64 = await fileToBase64(image);
                imagesBase64[i].push(base64);
              } catch (err) {
                // Se não conseguir converter, pular
                console.warn(`Erro ao converter imagem do setor ${i}`, err);
              }
            }
          }
        }
      }

      const draftData: SavedDraftData = {
        formValues: {
          ...formValues,
          sectors: sectors.map(s => ({
            ...s,
            images: [], // Não salvar objetos File
            imagesPreview: [], // Será reconstruído do base64
          })),
        },
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        selectedServicesForm,
        idCostumer,
        // Usar refs para garantir valores mais recentes das assinaturas
        technicianSignature: technicianSignatureRef.current,
        responsibleSignature: responsibleSignatureRef.current,
        imagesBase64: {}, // Imagens são salvas no IndexedDB separadamente
        savedAt: Date.now(),
      };

      // Salvar imagens no IndexedDB (limite muito maior que localStorage)
      if (Object.keys(imagesBase64).length > 0) {
        try {
          await saveImagesToIndexedDB(imagesBase64);
        } catch (indexedDBError) {
          console.warn("Falha ao salvar imagens no IndexedDB:", indexedDBError);
          // Se IndexedDB falhar, tentar localStorage como fallback (pode falhar se muito grande)
          draftData.imagesBase64 = imagesBase64;
        }
      }

      // Salvar metadados no localStorage (pequeno, não deve estourar limite)
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
      setLastSavedTime(new Date());

      // Debug: mostrar quantas imagens foram salvas por setor (comentado para produção)
      // const imagensInfo = Object.entries(imagesBase64)
      //   .map(([idx, imgs]) => `Setor ${Number(idx) + 1}: ${imgs.length} foto(s)`)
      //   .join(", ");
      // enqueueSnackbar(
      //   `Rascunho auto-salvo! ${imagensInfo || "Sem fotos"}`,
      //   { variant: "info", autoHideDuration: 2000 }
      // );
    } catch (error) {
      // Se falhar (ex: localStorage cheio), apenas logar
      // eslint-disable-next-line no-console
      console.warn("Não foi possível salvar rascunho:", error);
      enqueueSnackbar("Erro ao salvar rascunho automaticamente", { variant: "error" });
    }
  }, [id, startDate, endDate, selectedServicesForm, idCostumer]);

  // Função para carregar rascunho do localStorage
  const loadDraft = useCallback(async (): Promise<SavedDraftData | null> => {
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!saved) return null;

      const draftData: SavedDraftData = JSON.parse(saved);

      // Verificar se o rascunho não é muito antigo (máximo 24 horas)
      const maxAge = 24 * 60 * 60 * 1000;
      if (Date.now() - draftData.savedAt > maxAge) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        await clearIndexedDB();
        return null;
      }

      // Carregar imagens do IndexedDB (se existirem)
      if (Object.keys(draftData.imagesBase64).length === 0) {
        try {
          const imagesFromDB = await loadImagesFromIndexedDB();
          if (Object.keys(imagesFromDB).length > 0) {
            draftData.imagesBase64 = imagesFromDB;
          }
        } catch (error) {
          console.warn("Erro ao carregar imagens do IndexedDB:", error);
        }
      }

      return draftData;
    } catch {
      return null;
    }
  }, []);

  // Função para limpar rascunho
  const clearDraft = useCallback(async () => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    await clearIndexedDB();
    setHasDraft(false);
  }, []);

  // Função para restaurar rascunho
  const restoreDraft = useCallback(async (draftData: SavedDraftData) => {
    try {
      // Restaurar datas
      setStartDate(new Date(draftData.startDate));
      setEndDate(new Date(draftData.endDate));

      // Restaurar serviços
      setSelectedServicesForm(draftData.selectedServicesForm);

      // Restaurar cliente
      setIdCostumer(draftData.idCostumer);

      // Restaurar assinaturas
      if (draftData.technicianSignature) {
        setTechnicianSignatureData(draftData.technicianSignature);
        setLocalSignatures(prev => ({ ...prev, technician_signature: draftData.technicianSignature }));
      }
      if (draftData.responsibleSignature) {
        setResponsibleSignatureData(draftData.responsibleSignature);
        setLocalSignatures(prev => ({ ...prev, responsible_signature: draftData.responsibleSignature }));
      }

      // Restaurar valores do formulário
      if (formikRef.current && draftData.formValues) {
        const sectors = draftData.formValues.sectors || [];

        // Reconstruir imagens do base64
        for (let i = 0; i < sectors.length; i++) {
          const imagesBase64 = draftData.imagesBase64[i] || [];
          const files: File[] = [];
          const previews: string[] = [];

          for (let j = 0; j < imagesBase64.length; j++) {
            const base64 = imagesBase64[j];
            try {
              const file = await base64ToFile(base64, `image_${i}_${j}.jpg`);
              files.push(file);
              previews.push(base64);
            } catch {
              // Se falhar, pular
            }
          }

          sectors[i].images = files;
          sectors[i].imagesPreview = previews;
        }

        // Aplicar valores restaurados
        formikRef.current.setValues({
          ...formikRef.current.values,
          ...draftData.formValues,
          sectors,
          services: draftData.selectedServicesForm as any,
          costumer_id: draftData.idCostumer,
        });
      }

      enqueueSnackbar("Rascunho restaurado com sucesso!", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });

      setShowDraftModal(false);
    } catch (error) {
      enqueueSnackbar("Erro ao restaurar rascunho", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    }
  }, []);

  // Verificar se existe rascunho ao carregar a página
  useEffect(() => {
    if (id === undefined) {
      loadDraft().then(draftData => {
        if (draftData) {
          setHasDraft(true);
          setShowDraftModal(true);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-save com debounce quando o formulário muda
  const debouncedSaveRef = useRef<NodeJS.Timeout | null>(null);
  const saveDraftRef = useRef(saveDraft);

  // Manter ref atualizada
  useEffect(() => {
    saveDraftRef.current = saveDraft;
  }, [saveDraft]);

  const triggerAutoSave = useCallback(() => {
    if (id !== undefined) return; // Não auto-save em edição

    // Cancelar o debounce anterior
    if (debouncedSaveRef.current) {
      clearTimeout(debouncedSaveRef.current);
    }

    // Novo debounce de 2 segundos após última alteração
    debouncedSaveRef.current = setTimeout(() => {
      if (formikRef.current) {
        saveDraftRef.current(formikRef.current.values, formikRef.current.values.sectors);
      }
    }, 2000);
  }, [id]);

  // Limpar timeout ao desmontar
  useEffect(() => {
    return () => {
      if (debouncedSaveRef.current) {
        clearTimeout(debouncedSaveRef.current);
      }
    };
  }, []);

  // Monitorar visibilidade da página para detectar quando usuário volta
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setLastActiveTime(prev => {
          const now = Date.now();
          const inactiveTime = now - prev;
          const fiveMinutes = 5 * 60 * 1000;

          // Se ficou mais de 5 minutos inativo, mostrar aviso
          if (inactiveTime > fiveMinutes && id === undefined) {
            enqueueSnackbar(
              "Você ficou um tempo sem usar o app. Verifique se as imagens e assinaturas ainda estão corretas antes de salvar.",
              {
                variant: "warning",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                autoHideDuration: 8000,
              }
            );
          }
          return now;
        });
      } else {
        setLastActiveTime(Date.now());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id !== undefined) {
      setLoading(true);
      api.get(`techVisitReport/${id}`)
        .then(response => {
          const serviceOptions = response.data.services.map(
            ({ id: service_id, name }: SelectServices) => {
              return {
                value: service_id,
                label: name,
              };
            },
          );

          const servicesIds = response.data.services.map(
            ({ id: service_id }: SelectServices) => {
              return service_id;
            },
          );

          const { begin, end } = response.data;

          setSelectedServicesForm(servicesIds);
          setSelectedServices(serviceOptions);
          setStartDate(new Date(begin));
          setEndDate(new Date(end));
          setIdCostumer(response.data.costumer_id);

          // Carregar assinaturas existentes
          if (response.data.technician_signature) {
            // Se for uma URL, converter para exibição; se for base64, usar diretamente
            const technicianSig = response.data.technician_signature.startsWith('http')
              ? response.data.technician_signature
              : response.data.technician_signature;
            setTechnicianSignatureData(technicianSig);
            setLocalSignatures(prev => ({ ...prev, technician_signature: technicianSig }));
          }
          if (response.data.responsible_signature) {
            // Se for uma URL, converter para exibição; se for base64, usar diretamente
            const responsibleSig = response.data.responsible_signature.startsWith('http')
              ? response.data.responsible_signature
              : response.data.responsible_signature;
            setResponsibleSignatureData(responsibleSig);
            setLocalSignatures(prev => ({ ...prev, responsible_signature: responsibleSig }));
          }

          setReport(response.data);
        })
        .catch(error => {
          console.error("Erro ao carregar relatório:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [refresh, id]);

  useEffect(() => {
    api.get("/costumers", {
      params: {
        status: "A",
      },
    });
  }, [modalAddCostumer]);

  useEffect(() => {
    api
      .get("/services", {
        params: {
          status: "A",
        },
      })
      .then(response => {
        const serviceOptions = response.data.map(
          ({ id: service_id, name }: SelectServices) => {
            return {
              value: service_id,
              label: name,
            };
          },
        );

        return setSelectServices(serviceOptions);
      });

    api
      .get("/equipments", {
        params: {
          status: "A",
        },
      })
      .then(response => {
        const equipmentForm = response.data.map(
          (equipment: SelectEquipments) => {
            return {
              equipment_id: equipment.id,
              name: equipment.name,
              grade: "",
            };
          },
        );

        return setSelectEquipments(equipmentForm);
      });

    api
      .get("/users", {
        params: { status: "A" },
      })
      .then(response => {
        return setTechnicians(response.data);
      });
  }, []);

  useEffect(() => {
    api
      .get("/sectors", {
        params: {
          costumer_id: idCostumer,
          status: "A",
        },
      })
      .then(response => {
        return setSelectSectors(response.data.data || []);
      });

    if (idCostumer) {
      api
        .get("/responsibles", {
          params: { costumer_id: idCostumer, status: "A" },
        })
        .then(response => {
          return setResponsibles(response.data.data || []);
        });

      // Carregar observações emergenciais do cliente
      api
        .get(`/costumer-observations/${idCostumer}/open`)
        .then(response => {
          const observations = response.data || [];
          setEmergencyObservations(observations);
          setHasEmergency(observations.length > 0);

          // Mostrar notificação se houver emergências
          if (observations.length > 0) {
            enqueueSnackbar(
              `Este cliente possui ${observations.length} observação(ões) emergencial(is) em aberto!`,
              {
                variant: "warning",
                autoHideDuration: 5000,
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "center",
                },
              }
            );
          }
        })
        .catch(() => {
          setEmergencyObservations([]);
          setHasEmergency(false);
        });
    } else {
      setEmergencyObservations([]);
      setHasEmergency(false);
    }
  }, [idCostumer, modalAddSector, modalAddResponsible]);

  // Fechar painel de emergência ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emergencyPanelRef.current &&
        !emergencyPanelRef.current.contains(event.target as Node)
      ) {
        setShowEmergencyPanel(false);
      }
    };

    if (showEmergencyPanel) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmergencyPanel]);

  // Função para salvar assinaturas localmente
  const handleSaveSignatureLocal = useCallback((signatureData: string, type: string) => {
    setLocalSignatures(prev => ({
      ...prev,
      [type]: signatureData,
    }));

    // Também salvar nos estados individuais para visualização
    if (type === "technician_signature") {
      setTechnicianSignatureData(signatureData);
    } else if (type === "responsible_signature") {
      setResponsibleSignatureData(signatureData);
    }

    // Fechar modal
    setModalAddSignature(false);

    // Trigger manual do auto-save após salvar assinatura
    // As assinaturas estão fora do Formik, então precisamos disparar manualmente
    setTimeout(() => triggerAutoSave(), 100);
  }, [triggerAutoSave]);

  // Função para confirmar exclusão de assinatura
  const handleConfirmDelete = useCallback(() => {
    if (confirmDelete.type === "technician") {
      setTechnicianSignatureData("");
      setLocalSignatures(prev => ({ ...prev, technician_signature: undefined }));
    } else if (confirmDelete.type === "responsible") {
      setResponsibleSignatureData("");
      setLocalSignatures(prev => ({ ...prev, responsible_signature: undefined }));
    }
    setConfirmDelete({ show: false, type: "" });

    // Trigger manual do auto-save após excluir assinatura
    setTimeout(() => triggerAutoSave(), 100);
  }, [confirmDelete.type, triggerAutoSave]);

  // Função para abrir modal de assinatura
  const openSignatureModal = useCallback((type: string) => {
    setSignatureType(type);
    setModalAddSignature(true);
  }, []);

  // Toggle expandir/minimizar observação
  const toggleObservationExpand = useCallback((observationId: string) => {
    setExpandedObservations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(observationId)) {
        newSet.delete(observationId);
      } else {
        newSet.add(observationId);
      }
      return newSet;
    });
  }, []);

  // Toggle selecionar observação para fechar
  const toggleObservationSelect = useCallback((observationId: string) => {
    setSelectedObservationsToClose(prev => {
      const newSet = new Set(prev);
      if (newSet.has(observationId)) {
        newSet.delete(observationId);
      } else {
        newSet.add(observationId);
      }
      return newSet;
    });
  }, []);

  // Fechar observações selecionadas
  const closeSelectedObservations = useCallback(async () => {
    if (selectedObservationsToClose.size === 0) return;

    try {
      await api.patch("/costumer-observations/close", {
        observation_ids: Array.from(selectedObservationsToClose),
        costumer_id: idCostumer,
      });

      // Atualizar lista de observações
      const remaining = emergencyObservations.filter(
        obs => !selectedObservationsToClose.has(obs.id)
      );
      setEmergencyObservations(remaining);
      setHasEmergency(remaining.length > 0);
      setSelectedObservationsToClose(new Set());
      setShowEmergencyConfirmModal(false);

      enqueueSnackbar("Observações fechadas com sucesso!", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Erro ao fechar observações", {
        variant: "error",
      });
    }
  }, [selectedObservationsToClose, emergencyObservations, idCostumer]);

  // Função para abrir preview de imagem
  const openImagePreview = useCallback((src: string, alt: string) => {
    setImagePreview({ show: true, src, alt });
  }, []);

  // Função para fechar preview de imagem
  const closeImagePreview = useCallback(() => {
    setImagePreview({ show: false, src: "", alt: "" });
  }, []);

  // Função para processar múltiplas imagens com compressão
  // Corrigido para iOS/iPhone: usa base64 para persistência e força conversão para JPEG
  const processMultipleImages = useCallback(async (
    files: FileList | File[],
    sectorIndex: number,
    setFieldValue: (field: string, value: any) => void,
    currentImages: (File | ImageFile)[],
    currentPreviews: string[]
  ) => {
    const fileArray = Array.from(files);
    // Aceitar também arquivos HEIC/HEIF do iPhone
    const imageFiles = fileArray.filter(file =>
      file.type.startsWith('image/') ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif')
    );

    if (imageFiles.length === 0) {
      enqueueSnackbar('Nenhum arquivo de imagem válido selecionado.', {
        variant: 'warning',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }

    // Limite de 10 fotos por setor
    const maxImages = 10;
    const currentCount = currentImages.filter(img => img instanceof File || (img as ImageFile).file_name).length;
    const availableSlots = maxImages - currentCount;

    if (availableSlots <= 0) {
      enqueueSnackbar(`Limite de ${maxImages} fotos por setor atingido.`, {
        variant: 'warning',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }

    const filesToProcess = imageFiles.slice(0, availableSlots);

    if (filesToProcess.length < imageFiles.length) {
      enqueueSnackbar(`Apenas ${filesToProcess.length} de ${imageFiles.length} fotos serão adicionadas (limite: ${maxImages}).`, {
        variant: 'info',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    }

    setUploadingImages(prev => ({ ...prev, [sectorIndex]: true }));
    setUploadProgress(prev => ({ ...prev, [sectorIndex]: { current: 0, total: filesToProcess.length } }));

    // Forçar saída como JPEG para compatibilidade com iOS
    const compressionOptions = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/jpeg' as const,
    };

    const newImages: File[] = [];
    const newPreviews: string[] = [];

    for (let i = 0; i < filesToProcess.length; i++) {
      const file = filesToProcess[i];
      setUploadProgress(prev => ({ ...prev, [sectorIndex]: { current: i + 1, total: filesToProcess.length } }));

      try {
        // Comprimir imagem (retorna Blob) - forçando conversão para JPEG
        const compressedBlob = await imageCompression(file, compressionOptions);

        // IMPORTANTE para iOS: Converter Blob para base64 e depois para File
        // Isso garante que o arquivo seja persistente na memória do Safari
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(compressedBlob);
        });

        // Criar um novo File a partir do base64 (mais estável no iOS)
        const response = await fetch(base64);
        const blob = await response.blob();

        const compressedFile = new File(
          [blob],
          file.name.replace(/\.(heic|heif)$/i, '.jpg').replace(/\.[^.]+$/, '.jpg'),
          {
            type: 'image/jpeg',
            lastModified: Date.now(),
          }
        );

        newImages.push(compressedFile);
        // Usar base64 como preview em vez de blob URL (mais estável no iOS Safari)
        newPreviews.push(base64);

      } catch (error) {
        console.warn(`Erro ao processar imagem ${file.name}:`, error);

        // Fallback: tentar usar arquivo original convertido para base64
        try {
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const response = await fetch(base64);
          const blob = await response.blob();
          const fallbackFile = new File([blob], file.name, {
            type: file.type || 'image/jpeg',
            lastModified: Date.now(),
          });

          newImages.push(fallbackFile);
          newPreviews.push(base64);
        } catch (fallbackError) {
          console.error(`Falha total ao processar ${file.name}:`, fallbackError);
          enqueueSnackbar(`Erro ao processar imagem: ${file.name}`, {
            variant: 'error',
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          });
        }
      }
    }

    // Filtrar imagens vazias (slots não usados) e adicionar as novas
    const validCurrentImages = currentImages.filter(img => img instanceof File || (img as ImageFile).file_name);
    const validCurrentPreviews = currentPreviews.filter(preview => preview && preview.length > 0);

    const updatedImages = [...validCurrentImages, ...newImages];
    const updatedPreviews = [...validCurrentPreviews, ...newPreviews];

    setFieldValue(`sectors[${sectorIndex}].images`, updatedImages);
    setFieldValue(`sectors[${sectorIndex}].imagesPreview`, updatedPreviews);

    setUploadingImages(prev => ({ ...prev, [sectorIndex]: false }));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[sectorIndex];
      return newProgress;
    });

    if (newImages.length > 0) {
      enqueueSnackbar(`${newImages.length} foto(s) adicionada(s) com sucesso!`, {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 2000,
      });
    }

    // Trigger auto-save
    setTimeout(() => triggerAutoSave(), 100);
  }, [triggerAutoSave]);

  // Handlers para drag & drop
  const handleDragOver = useCallback((e: React.DragEvent, sectorIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(prev => ({ ...prev, [sectorIndex]: true }));
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent, sectorIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(prev => ({ ...prev, [sectorIndex]: false }));
  }, []);

  const handleDrop = useCallback((
    e: React.DragEvent,
    sectorIndex: number,
    setFieldValue: (field: string, value: any) => void,
    currentImages: (File | ImageFile)[],
    currentPreviews: string[]
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(prev => ({ ...prev, [sectorIndex]: false }));

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processMultipleImages(files, sectorIndex, setFieldValue, currentImages, currentPreviews);
    }
  }, [processMultipleImages]);

  // Effect para detectar tecla ESC e fechar modal de preview
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && imagePreview.show) {
        closeImagePreview();
      }
    };

    if (imagePreview.show) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [imagePreview.show, closeImagePreview]);

  // Effect para limpar URLs de blob quando componente for desmontado
  useEffect(() => {
    return () => {
      // Limpar todas as URLs de blob ao desmontar componente
      if (report?.sectors) {
        report.sectors.forEach(sector => {
          if (sector.imagesPreview) {
            sector.imagesPreview.forEach(url => {
              if (url && url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
              }
            });
          }
        });
      }
    };
  }, [report?.sectors]);

  // Função para fazer upload de assinatura
  const uploadSignature = async (
    signatureData: string,
    type: string,
    reportId: string,
  ) => {
    // Converter base64 para blob
    const response = await fetch(signatureData);
    const blob = await response.blob();

    // Criar FormData
    const formData = new FormData();
    formData.append("signature_file", blob, `${type}.png`);

    // Fazer upload usando PATCH conforme a rota do backend
    await api.patch(`/techVisitReport/${type}/${reportId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  async function handleSubmit({
    costumer_id,
    begin,
    end,
    observation,
    pending,
    sectors,
    services,
    responsible_id,
    technician_id,
    emergency_observations,
  }: FormTechVisitReport) {
    // Prevenção contra duplo clique
    if (isSubmitting) {
      enqueueSnackbar(
        "Aguarde, o relatório está sendo processado...",
        {
          variant: "info",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        },
      );
      return;
    }

    if (!technicianSignatureData || !responsibleSignatureData) {
      enqueueSnackbar(
        "É necessário coletar ambas as assinaturas antes de salvar o relatório",
        {
          variant: "warning",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        },
      );
      return;
    }

    // Se há observações emergenciais e não mostrou a modal ainda
    if (hasEmergency && emergencyObservations.length > 0 && !pendingFormData) {
      setPendingFormData({
        costumer_id,
        user_id: user.id,
        begin,
        end,
        observation,
        pending,
        sectors,
        services,
        responsible_id,
        technician_id,
        responsible_signature: responsibleSignatureData,
        technician_signature: technicianSignatureData,
        emergency_observations,
      });
      setShowEmergencyConfirmModal(true);
      return;
    }

    await processSubmit({
      costumer_id,
      begin,
      end,
      observation,
      pending,
      sectors,
      services,
      responsible_id,
      technician_id,
      emergency_observations,
    });
  }

  // Função para validar se os arquivos ainda são acessíveis (importante para iOS)
  const validateFiles = async (sectors: any[]): Promise<{ valid: boolean; invalidCount: number }> => {
    let invalidCount = 0;

    for (const sector of sectors) {
      if (sector.images) {
        for (const file of sector.images) {
          if (file instanceof File) {
            try {
              // Verificar se o arquivo tem tamanho válido
              if (file.size === 0) {
                invalidCount++;
                continue;
              }

              // Tentar ler os primeiros bytes do arquivo para verificar se ainda é acessível
              // Isso é importante no iOS onde referências de arquivo podem ser perdidas
              const testSlice = file.slice(0, 1);
              await new Promise<void>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve();
                reader.onerror = () => reject(new Error('Arquivo não acessível'));
                reader.readAsArrayBuffer(testSlice);
              });
            } catch {
              invalidCount++;
            }
          }
        }
      }
    }

    return { valid: invalidCount === 0, invalidCount };
  };

  // Função para validar assinaturas
  const validateSignatures = (): boolean => {
    // Verificar se as assinaturas são strings base64 válidas
    if (localSignatures.technician_signature) {
      if (!localSignatures.technician_signature.startsWith('data:image')) {
        return false;
      }
    }
    if (localSignatures.responsible_signature) {
      if (!localSignatures.responsible_signature.startsWith('data:image')) {
        return false;
      }
    }
    return true;
  };

  // Função real de processamento do submit
  async function processSubmit({
    costumer_id,
    begin,
    end,
    observation,
    pending,
    sectors,
    services,
    responsible_id,
    technician_id,
    emergency_observations,
  }: Omit<FormTechVisitReport, 'user_id' | 'responsible_signature' | 'technician_signature'>) {
    setIsSubmitting(true);
    setLoading(true);

    // Validar arquivos antes de enviar
    if (id === undefined) {
      const { valid, invalidCount } = await validateFiles(sectors);
      if (!valid) {
        enqueueSnackbar(
          `${invalidCount} imagem(ns) não está(ão) mais acessível(is). Por favor, adicione novamente.`,
          {
            variant: "warning",
            anchorOrigin: { vertical: "top", horizontal: "right" },
          }
        );
        setLoading(false);
        setIsSubmitting(false);
        return;
      }

      // Validar assinaturas
      if (!validateSignatures()) {
        enqueueSnackbar(
          "As assinaturas não estão mais válidas. Por favor, colete novamente.",
          {
            variant: "warning",
            anchorOrigin: { vertical: "top", horizontal: "right" },
          }
        );
        setLoading(false);
        setIsSubmitting(false);
        return;
      }
    }

    const servicesFormated = services.map(service => {
      return { service_id: service };
    });

    try {
      if (id === undefined) {
        // Limpar imagens dos setores antes de enviar JSON (serão enviadas via FormData depois)
        const sectorsWithoutImages = sectors.map(sector => ({
          ...sector,
          images: [], // Remover imagens do JSON - serão enviadas separadamente
          imagesPreview: [], // Também remover previews
        }));

        // Criar novo relatório
        const { report_sector_ids, report_id } = await api
          .post("/techVisitReport/unified", {
            costumer_id,
            user_id: user.id,
            begin,
            end,
            observation,
            pending,
            services: servicesFormated,
            sectors: sectorsWithoutImages,
            responsible_id,
            technician_id,
          })
          .then(response => {
            return response.data;
          });

        // Upload das imagens dos setores
        const promise = sectors.map(async ({ sector_id, images }) => {
          const { report_sector_id } = report_sector_ids.find(
            element => element.sector_id === sector_id,
          );

          // Filtrar apenas arquivos File válidos
          const validFiles = images.filter(file => file instanceof File && file.name);

          if (validFiles.length === 0) {
            return; // Sem imagens válidas para fazer upload
          }

          const promise2 = validFiles.map(async (file: File) => {
            try {
              const data = new FormData();

              // IMPORTANTE: Não comprimir novamente - as imagens já foram comprimidas
              // na função processMultipleImages quando foram selecionadas
              data.append("files", file);

              await api.post(
                `/techVisitReport/sector_images/${report_sector_id}`,
                data,
              );
            } catch (error) {
              console.error(`Erro ao fazer upload da imagem ${file.name}:`, error);
              // Continua com as outras imagens mesmo se uma falhar
            }
          });

          await Promise.all(promise2);
        });

        await Promise.all(promise);

        // Upload das assinaturas coletadas localmente
        if (localSignatures.technician_signature) {
          await uploadSignature(
            localSignatures.technician_signature,
            "technician_signature",
            report_id,
          );
        }

        if (localSignatures.responsible_signature) {
          await uploadSignature(
            localSignatures.responsible_signature,
            "responsible_signature",
            report_id,
          );
        }

        // Atualizar responsáveis (já incluindo as assinaturas)
        await api.patch(`/techVisitReport/responsibles/${report_id}`, {
          responsible_id,
          technician_id,
        });

        // Criar observações de emergência se preenchidas
        if (emergency_observations && emergency_observations.length > 0) {
          const validObservations = emergency_observations.filter(obs => obs.trim());
          await Promise.all(
            validObservations.map(async obsText => {
              try {
                await api.post("/costumer-observations", {
                  costumer_id,
                  text: obsText.trim(),
                });
              } catch (obsError) {
                // eslint-disable-next-line no-console
                console.error("Erro ao criar observação de emergência:", obsError);
              }
            }),
          );
        }

        enqueueSnackbar("Relatório cadastrado com sucesso!", {
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        });

        // Limpar rascunho após sucesso
        clearDraft();

        navigate(`/tech-visit-reports`);
      } else {
        // Atualizar relatório existente
        await api
          .put(`/techVisitReport/${id}`, {
            costumer_id,
            user_id: user.id,
            begin,
            end,
            observation,
            pending,
            services: servicesFormated,
            sectors,
          })
          .then(response => {
            return response.data;
          });

        // Atualizar responsáveis
        await api.patch(`/techVisitReport/responsibles/${id}`, {
          responsible_id,
          technician_id,
        });

        enqueueSnackbar("Relatório atualizado com sucesso!", {
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        });

        navigate(`/tech-visit-reports`);
      }
    } catch (error: any) {
      // Mensagem de erro mais específica
      let errorMessage = "Erro ao salvar o relatório. ";

      if (error.response?.status === 401) {
        errorMessage = "Sua sessão expirou. Por favor, faça login novamente.";
      } else if (error.response?.status === 413) {
        errorMessage = "As imagens são muito grandes. Tente reduzir o tamanho.";
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = "A conexão demorou muito. Verifique sua internet e tente novamente.";
      } else if (!navigator.onLine) {
        errorMessage = "Você está sem conexão com a internet.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      enqueueSnackbar(errorMessage, {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
        autoHideDuration: 6000,
      });

      // Log para debug
      // eslint-disable-next-line no-console
      console.error("Erro ao salvar relatório:", error);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  }

  async function deleteImage({ id: image_id }) {
    if (!image_id) {
      enqueueSnackbar('ID da imagem não fornecido', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/techVisitReport/sector_images/${image_id}`);

      setTimeout(() => {
        setRefresh(!refresh);
        return setLoading(false);
      }, 2000);
    } catch (error) {
      setLoading(false);
      enqueueSnackbar('Erro ao deletar a imagem. Tente novamente.', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    }
  }

  async function updateImage({ file, report_sector_id }) {
    if (!file || !report_sector_id) {
      enqueueSnackbar('Arquivo ou ID do setor não fornecido', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }

    try {
      setLoading(true);
      const data = new FormData();

      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };

      const fileCompressed = await imageCompression(file, options);
      data.append("files", fileCompressed);

      await api.post(`/techVisitReport/sector_images/${report_sector_id}`, data);

      setTimeout(() => {
        setRefresh(!refresh);
        return setLoading(false);
      }, 2000);
    } catch (error) {
      setLoading(false);
      enqueueSnackbar('Erro ao fazer upload da imagem. Tente novamente.', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    }
  }

  function verifyServicesSelected(option, setFieldValue) {
    if (option.length === 1 && option[0].label === "Entrega") {
      setFieldValue("observation", "");
    } else {
      setFieldValue(
        "observation",
        "Os demais setores e equipamentos estavam em bom estado de conservação e(ou) funcionamento.",
      );
    }
  }

  const initialValues: FormTechVisitReport = !report
    ? {
        begin: "",
        end: "",
        costumer_id: "",
        user_id: user.id,
        services: [],
        sectors: [],
        pending: "",
        observation:
          "Os demais setores e equipamentos estavam em bom estado de conservação e(ou) funcionamento.",
        responsible_id: "",
        responsible_signature: "",
        technician_id: "",
        technician_signature: "",
        emergency_observations: [],
      }
    : {
        begin: report.begin,
        end: report.end,
        costumer_id: report.costumer_id,
        user_id: report.user_id,
        services: selectedServicesForm,
        sectors: report.sectors,
        pending: report.pending,
        observation: report.observation,
        responsible_id: report.responsible_id,
        responsible_signature: report.responsible_signature,
        technician_id: report.technician_id,
        technician_signature: report.technician_signature,
        emergency_observations: [],
      };

  return (
    <>
      {loading && <Loading />}

      {/* Modal de Rascunho Encontrado */}
      <Modal
        isOpen={showDraftModal}
        onClose={() => {
          setShowDraftModal(false);
          clearDraft();
        }}
        title="Rascunho Encontrado"
        variant="warning"
      >
        <div className="flex flex-col gap-4">
          <p className="text-gray-600">
            Encontramos um rascunho de relatório que você estava preenchendo anteriormente. Deseja continuar de onde parou?
          </p>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={async () => {
                const draftData = await loadDraft();
                if (draftData) {
                  restoreDraft(draftData);
                }
              }}
              className="w-full py-3.5 px-4 bg-gradient-to-r from-[#0085BD] to-[#00a6e8] hover:from-[#006d9c] hover:to-[#0085BD] text-white font-semibold rounded-xl shadow-lg shadow-[#0085BD]/30 hover:shadow-xl hover:shadow-[#0085BD]/40 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <FiCheck size={18} />
              Continuar Rascunho
            </button>
            <button
              type="button"
              onClick={() => {
                clearDraft();
                setShowDraftModal(false);
              }}
              className="w-full py-3.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl border border-gray-300 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <FiX size={18} />
              Começar Novo
            </button>
          </div>
        </div>
      </Modal>

      <div className="container mx-auto">
        <PageTitle
          title={id ? "Editar Relatório" : "Novo Relatório"}
          backButton
          backPath="/tech-visit-reports"
        />

        {/* Indicador de auto-save */}
        {id === undefined && lastSavedTime && (
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
            <FiSave size={12} />
            <span>Rascunho salvo às {format(lastSavedTime, "HH:mm:ss")}</span>
          </div>
        )}

        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          enableReinitialize
          validationSchema={schema}
          onSubmit={values => {
            handleSubmit(values);
          }}
        >
          {({ errors, values, setFieldValue, setFieldTouched }) => (
            <Form className="flex flex-col flex-wrap space-y-4">
              {/* Auto-save quando valores mudam */}
              {id === undefined && <AutoSaveFormik onSave={triggerAutoSave} />}

              {/* Seção 1: Dados Básicos */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4 rounded-t-2xl">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    Dados da Visita
                  </h3>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-2 block">
                        Data e hora do início da visita
                      </label>
                      <DateHourPicker
                        selected={startDate}
                        onChange={date => {
                          setStartDate(date);
                          setFieldValue(
                            "begin",
                            format(date, "yyyy-MM-dd HH:mm"),
                          );
                        }}
                        timeInputLabel="Time:"
                        dateFormat="dd/MM/yyyy H:mm"
                        showTimeInput
                        error={errors.begin}
                      />
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-2 block">
                        Data e hora do término da visita
                      </label>
                      <DateHourPicker
                        selected={endDate}
                        onChange={date => {
                          setEndDate(date);
                          setFieldValue("end", format(date, "yyyy-MM-dd HH:mm"));
                        }}
                        timeInputLabel="Time:"
                        dateFormat="dd/MM/yyyy H:mm"
                        showTimeInput
                        error={errors.end}
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">
                      Cliente
                    </label>
                    <div className="flex gap-2">
                      <SelectSearch
                        name="costumer_id"
                        placeholder="Digite o nome do cliente..."
                        value={idCostumer}
                        onChange={value => {
                          setFieldValue("costumer_id", value);
                          setFieldTouched("costumer_id", true);
                          setIdCostumer(value);
                        }}
                        error={errors.costumer_id}
                      />
                      <AddButton
                        onClick={() => setModalAddCostumer(true)}
                        tooltip="Adicionar novo cliente"
                        size="md"
                      />
                      {modalAddCostumer && (
                        <ModalAddCostumer
                          setModalAddCostumer={setModalAddCostumer}
                        />
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">
                      Serviços
                    </label>
                    <MultiSelectDropdown
                      name="services"
                      options={selectServices.map(s => ({
                        value: s.value || s.id,
                        label: s.label || s.name,
                      }))}
                      value={selectedServicesForm}
                      placeholder="Selecione os serviços..."
                      onChange={selectedValues => {
                        setSelectedServicesForm(selectedValues);
                        const selectedOptions = selectServices.filter(opt =>
                          selectedValues.includes(opt.value || opt.id),
                        );
                        setSelectedServices(selectedOptions);
                        setFieldValue("services", selectedValues);
                        verifyServicesSelected(selectedOptions, setFieldValue);
                      }}
                      error={
                        typeof errors.services === "string"
                          ? errors.services
                          : undefined
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Seção 2: Setores */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-4 sm:px-6 py-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Setores Visitados
                  </h3>
                </div>

                <div className="p-3 sm:p-6">
                  <FieldArray
                    name="sectors"
                    render={arraySectors => (
                      <div className="space-y-4">
                        {values.sectors && values.sectors.length > 0 ? (
                          values.sectors.map(
                            (
                              { show, id: sectorId }: FormSectors,
                              indexSector,
                            ) => (
                              <div
                                className="relative rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow mt-6 min-w-0"
                                key={sectorId || `sector-${indexSector}`}
                              >
                                {/* Botão Minimizar/Maximizar - Centro Superior */}
                                <button
                                  type="button"
                                  className="absolute left-1/2 -translate-x-1/2 -top-4 z-10 w-8 h-8 flex items-center justify-center bg-[#0085BD] hover:bg-[#006a96] rounded-full shadow-lg transition-all hover:scale-110"
                                  onClick={() =>
                                    setFieldValue(
                                      `sectors[${indexSector}].show`,
                                      !show,
                                    )
                                  }
                                  title={show ? "Recolher" : "Expandir"}
                                >
                                  <IoIosArrowDown
                                    size={18}
                                    className={`text-white transition-transform duration-300 ${show ? "rotate-180" : "rotate-0"}`}
                                  />
                                </button>

                                {/* Botão Excluir - Canto Superior Esquerdo */}
                                <button
                                  type="button"
                                  onClick={() =>
                                    arraySectors.remove(indexSector)
                                  }
                                  className="absolute -top-3 -left-3 z-10 w-8 h-8 flex items-center justify-center bg-red-500 hover:bg-red-600 rounded-full shadow-lg transition-all hover:scale-110"
                                  title="Remover setor"
                                >
                                  <FiTrash size={14} className="text-white" />
                                </button>

                                {/* Header do Setor */}
                                <div className="bg-gradient-to-r from-[#0085BD] to-[#00a8e8] p-4 pt-6 rounded-t-xl">
                                  <div>
                                    <label className="text-xs font-medium text-white/80 uppercase tracking-wide mb-1 block">
                                      Setor
                                    </label>
                                    <div className="flex gap-2">
                                        <SelectSearchSector
                                          name={`sectors[${indexSector}].sector_id`}
                                          placeholder="Digite o nome do setor..."
                                          value={values.sectors[indexSector].sector_id}
                                          costumerId={idCostumer}
                                          onChange={value => {
                                            setFieldValue(`sectors[${indexSector}].sector_id`, value);
                                          }}
                                        />
                                        <AddButton
                                          onClick={() => setModalAddSector(true)}
                                          tooltip="Cadastrar novo setor"
                                          size="md"
                                        />
                                        {modalAddSector && (
                                          <ModalAddSector
                                            setModalAddSector={setModalAddSector}
                                          />
                                        )}
                                      </div>
                                  </div>
                                </div>

                                {/* Conteúdo Expansível */}
                                <div
                                  className={`transition-all duration-300 ease-in-out ${
                                    values.sectors[indexSector].show
                                      ? "max-h-[3000px] opacity-100"
                                      : "max-h-0 opacity-0 overflow-hidden"
                                  }`}
                                >
                                  <div className="p-4 bg-gray-50/50">
                                    {/* Tabela de Equipamentos - Mobile Friendly */}
                                    <div className="mb-4">
                                      <div className="flex items-center gap-2 mb-3">
                                        <svg className="w-4 h-4 text-[#0085BD]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                                        </svg>
                                        <span className="text-sm font-semibold text-gray-700">Avaliação dos Equipamentos</span>
                                      </div>

                                      {/* Legenda - Mobile */}
                                      <div className="sm:hidden flex items-center justify-end gap-3 mb-2 px-2 py-1.5 bg-gray-100 rounded-lg text-[10px]">
                                        <span className="text-gray-500 font-medium">Legenda:</span>
                                        <span className="flex items-center gap-1">
                                          <span className="w-4 h-4 rounded bg-green-500 text-white flex items-center justify-center font-bold">O</span>
                                          <span className="text-gray-600">Ótimo</span>
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <span className="w-4 h-4 rounded bg-yellow-500 text-white flex items-center justify-center font-bold">B</span>
                                          <span className="text-gray-600">Bom</span>
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <span className="w-4 h-4 rounded bg-red-500 text-white flex items-center justify-center font-bold">R</span>
                                          <span className="text-gray-600">Revisado</span>
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <span className="w-4 h-4 rounded bg-blue-500 text-white flex items-center justify-center font-bold">S</span>
                                          <span className="text-gray-600">Substituído</span>
                                        </span>
                                      </div>

                                      {/* Header da Tabela - Desktop */}
                                      <div className="hidden sm:grid grid-cols-[1fr,repeat(5,48px)] gap-2 mb-2 px-3 py-2 bg-[#0085BD]/10 rounded-lg">
                                        <span className="text-xs font-bold text-[#0085BD] uppercase">Equipamento</span>
                                        <span className="text-xs font-bold text-green-600 text-center" title="Ótimo">O</span>
                                        <span className="text-xs font-bold text-yellow-600 text-center" title="Bom">B</span>
                                        <span className="text-xs font-bold text-red-600 text-center" title="Revisado">R</span>
                                        <span className="text-xs font-bold text-blue-600 text-center" title="Substituído">S</span>
                                        <span className="text-xs font-bold text-gray-400 text-center">⌫</span>
                                      </div>

                                      {/* Lista de Equipamentos */}
                                      <div className="space-y-1">
                                        {selectEquipments.map(
                                          (
                                            { name: equipmentName },
                                            indexEquipment,
                                          ) => (
                                            <div
                                              key={`equip-${indexSector}-${indexEquipment}`}
                                              className={`rounded-lg border border-gray-200 p-2 sm:p-3 hover:border-[#0085BD]/30 transition-colors ${
                                                indexEquipment % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                                              }`}
                                            >
                                              {/* Mobile Layout - Compacto em linha */}
                                              <div className="sm:hidden flex items-center justify-between gap-2">
                                                <span className="text-xs font-medium text-gray-700 flex-1 truncate">{equipmentName}</span>
                                                <div className="flex items-center gap-1">
                                                  <label className={`flex items-center justify-center w-8 h-8 rounded-md cursor-pointer transition-all ${
                                                    values.sectors[indexSector]?.equipments?.[indexEquipment]?.grade === 'O'
                                                      ? 'bg-green-500 text-white shadow-sm'
                                                      : 'bg-gray-100 text-gray-400 active:bg-green-100'
                                                  }`}>
                                                    <Field
                                                      type="radio"
                                                      value="O"
                                                      name={`sectors[${indexSector}].equipments[${indexEquipment}].grade`}
                                                      className="sr-only"
                                                    />
                                                    <span className="text-xs font-bold">O</span>
                                                  </label>
                                                  <label className={`flex items-center justify-center w-8 h-8 rounded-md cursor-pointer transition-all ${
                                                    values.sectors[indexSector]?.equipments?.[indexEquipment]?.grade === 'B'
                                                      ? 'bg-yellow-500 text-white shadow-sm'
                                                      : 'bg-gray-100 text-gray-400 active:bg-yellow-100'
                                                  }`}>
                                                    <Field
                                                      type="radio"
                                                      value="B"
                                                      name={`sectors[${indexSector}].equipments[${indexEquipment}].grade`}
                                                      className="sr-only"
                                                    />
                                                    <span className="text-xs font-bold">B</span>
                                                  </label>
                                                  <label className={`flex items-center justify-center w-8 h-8 rounded-md cursor-pointer transition-all ${
                                                    values.sectors[indexSector]?.equipments?.[indexEquipment]?.grade === 'R'
                                                      ? 'bg-red-500 text-white shadow-sm'
                                                      : 'bg-gray-100 text-gray-400 active:bg-red-100'
                                                  }`}>
                                                    <Field
                                                      type="radio"
                                                      value="R"
                                                      name={`sectors[${indexSector}].equipments[${indexEquipment}].grade`}
                                                      className="sr-only"
                                                    />
                                                    <span className="text-xs font-bold">R</span>
                                                  </label>
                                                  <label className={`flex items-center justify-center w-8 h-8 rounded-md cursor-pointer transition-all ${
                                                    values.sectors[indexSector]?.equipments?.[indexEquipment]?.grade === 'S'
                                                      ? 'bg-blue-500 text-white shadow-sm'
                                                      : 'bg-gray-100 text-gray-400 active:bg-blue-100'
                                                  }`}>
                                                    <Field
                                                      type="radio"
                                                      value="S"
                                                      name={`sectors[${indexSector}].equipments[${indexEquipment}].grade`}
                                                      className="sr-only"
                                                    />
                                                    <span className="text-xs font-bold">S</span>
                                                  </label>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      setFieldValue(
                                                        `sectors[${indexSector}].equipments[${indexEquipment}].grade`,
                                                        "",
                                                      );
                                                    }}
                                                    className="w-6 h-8 flex items-center justify-center text-gray-300 active:text-red-500 transition-colors"
                                                    title="Limpar"
                                                  >
                                                    <FiX size={14} />
                                                  </button>
                                                </div>
                                              </div>

                                              {/* Desktop Layout */}
                                              <div className="hidden sm:grid grid-cols-[1fr,repeat(5,48px)] gap-2 items-center">
                                                <span className="text-sm font-medium text-gray-700">{equipmentName}</span>
                                                <label className={`flex items-center justify-center w-10 h-10 mx-auto rounded-lg cursor-pointer transition-all ${
                                                  values.sectors[indexSector]?.equipments?.[indexEquipment]?.grade === 'O'
                                                    ? 'bg-green-500 text-white shadow-md'
                                                    : 'bg-gray-100 text-gray-400 hover:bg-green-100 hover:text-green-600'
                                                }`}>
                                                  <Field
                                                    type="radio"
                                                    value="O"
                                                    name={`sectors[${indexSector}].equipments[${indexEquipment}].grade`}
                                                    className="sr-only"
                                                  />
                                                  <span className="text-sm font-bold">O</span>
                                                </label>
                                                <label className={`flex items-center justify-center w-10 h-10 mx-auto rounded-lg cursor-pointer transition-all ${
                                                  values.sectors[indexSector]?.equipments?.[indexEquipment]?.grade === 'B'
                                                    ? 'bg-yellow-500 text-white shadow-md'
                                                    : 'bg-gray-100 text-gray-400 hover:bg-yellow-100 hover:text-yellow-600'
                                                }`}>
                                                  <Field
                                                    type="radio"
                                                    value="B"
                                                    name={`sectors[${indexSector}].equipments[${indexEquipment}].grade`}
                                                    className="sr-only"
                                                  />
                                                  <span className="text-sm font-bold">B</span>
                                                </label>
                                                <label className={`flex items-center justify-center w-10 h-10 mx-auto rounded-lg cursor-pointer transition-all ${
                                                  values.sectors[indexSector]?.equipments?.[indexEquipment]?.grade === 'R'
                                                    ? 'bg-red-500 text-white shadow-md'
                                                    : 'bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-600'
                                                }`}>
                                                  <Field
                                                    type="radio"
                                                    value="R"
                                                    name={`sectors[${indexSector}].equipments[${indexEquipment}].grade`}
                                                    className="sr-only"
                                                  />
                                                  <span className="text-sm font-bold">R</span>
                                                </label>
                                                <label className={`flex items-center justify-center w-10 h-10 mx-auto rounded-lg cursor-pointer transition-all ${
                                                  values.sectors[indexSector]?.equipments?.[indexEquipment]?.grade === 'S'
                                                    ? 'bg-blue-500 text-white shadow-md'
                                                    : 'bg-gray-100 text-gray-400 hover:bg-blue-100 hover:text-blue-600'
                                                }`}>
                                                  <Field
                                                    type="radio"
                                                    value="S"
                                                    name={`sectors[${indexSector}].equipments[${indexEquipment}].grade`}
                                                    className="sr-only"
                                                  />
                                                  <span className="text-sm font-bold">S</span>
                                                </label>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setFieldValue(
                                                      `sectors[${indexSector}].equipments[${indexEquipment}].grade`,
                                                      "",
                                                    );
                                                  }}
                                                  className="flex items-center justify-center w-10 h-10 mx-auto rounded-lg text-gray-400 hover:bg-red-100 hover:text-red-500 transition-colors"
                                                  title="Limpar seleção"
                                                >
                                                  <FiX size={16} />
                                                </button>
                                              </div>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </div>

                                    {/* Observações do Setor */}
                                    <div className="mb-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <svg className="w-4 h-4 text-[#0085BD]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        <span className="text-sm font-semibold text-gray-700">Observações do Setor</span>
                                      </div>
                                      <Field
                                        as="textarea"
                                        placeholder="Adicione observações específicas deste setor..."
                                        name={`sectors[${indexSector}].observation`}
                                        className="w-full h-24 p-3 rounded-lg text-gray-700 bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-2 focus:ring-[#0085BD]/20 focus:outline-none resize-none text-sm transition-all"
                                      />
                                    </div>

                                    {/* Imagens do Setor */}
                                    <div>
                                      <div className="flex items-center gap-2 mb-2">
                                        <svg className="w-4 h-4 text-[#0085BD]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span className="text-sm font-semibold text-gray-700">Fotos do Setor</span>
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        <FieldArray
                                          name={`sectors[${indexSector}].images`}
                                          render={arrayImages => (
                                            <>
                                              {(values.sectors[indexSector].images || [])
                                                .map((file, originalIndex) => ({ file, originalIndex }))
                                                .filter(({ file }) => {
                                                  // Filtrar apenas imagens válidas (File ou ImageFile com file_name)
                                                  if (file instanceof File) return true;
                                                  if ((file as ImageFile)?.file_name) return true;
                                                  // Para novos uploads, verificar se há preview correspondente
                                                  return false;
                                                })
                                                .map(({ file, originalIndex: indexImage }) => {
                                                  const imageFile =
                                                    file as ImageFile;
                                                  return (
                                                    <div
                                                      className="relative group"
                                                      key={
                                                        imageFile.id ||
                                                        `image-${indexSector}-${indexImage}`
                                                      }
                                                    >
                                                      {!(
                                                        file instanceof File
                                                      ) &&
                                                      (file as ImageFile)
                                                        .file_name ? (
                                                        <button
                                                          type="button"
                                                          className="w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#0085BD] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0085BD]"
                                                          onClick={() => openImagePreview(
                                                            `https://app-luvilimp.s3.amazonaws.com/techVisitReportSectorImages/${imageFile.file_name}`,
                                                            `Imagem do setor ${indexSector + 1}`
                                                          )}
                                                          title="Clique para ampliar"
                                                          aria-label={`Ampliar imagem do setor ${indexSector + 1}`}
                                                        >
                                                          <img
                                                            className="w-full h-full object-cover"
                                                            src={`https://app-luvilimp.s3.amazonaws.com/techVisitReportSectorImages/${imageFile.file_name}`}
                                                            alt={`Imagem do setor ${indexSector + 1}`}
                                                          />
                                                        </button>
                                                      ) : file instanceof File && values.sectors[indexSector]?.imagesPreview?.[indexImage] ? (
                                                        // Preview para arquivos File (novos uploads)
                                                        <button
                                                          type="button"
                                                          className="w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#0085BD] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0085BD]"
                                                          onClick={() => openImagePreview(
                                                            values.sectors[indexSector].imagesPreview[indexImage],
                                                            `Preview da imagem ${indexImage + 1}`
                                                          )}
                                                          title="Clique para ampliar"
                                                          aria-label={`Ampliar preview da imagem ${indexImage + 1}`}
                                                        >
                                                          <img
                                                            className="w-full h-full object-cover"
                                                            src={values.sectors[indexSector].imagesPreview[indexImage]}
                                                            alt={`Preview da imagem ${indexImage + 1}`}
                                                          />
                                                        </button>
                                                      ) : null}
                                                      <button
                                                        type="button"
                                                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-md"
                                                        onClick={() => {
                                                          if (id === undefined) {
                                                            const previewUrl = values.sectors[indexSector]?.imagesPreview?.[indexImage];
                                                            if (previewUrl && previewUrl.startsWith('blob:')) {
                                                              URL.revokeObjectURL(previewUrl);
                                                            }

                                                            // Remover imagem e preview
                                                            const newImages = [...values.sectors[indexSector].images];
                                                            const newPreviews = [...values.sectors[indexSector].imagesPreview];
                                                            newImages.splice(indexImage, 1);
                                                            newPreviews.splice(indexImage, 1);
                                                            setFieldValue(`sectors[${indexSector}].images`, newImages);
                                                            setFieldValue(`sectors[${indexSector}].imagesPreview`, newPreviews);

                                                            // Trigger manual do auto-save após remover imagem
                                                            setTimeout(() => triggerAutoSave(), 100);
                                                          } else if (imageFile?.id) {
                                                            deleteImage({ id: imageFile.id });
                                                          }
                                                        }}
                                                        title="Remover imagem"
                                                      >
                                                        <FiX size={12} className="text-white" />
                                                      </button>
                                                    </div>
                                                  );
                                                },
                                              )}

                                                {/* Área de upload múltiplo com drag & drop */}
                                                {id === undefined && (
                                                  <div
                                                    className={`relative min-w-[120px] h-16 flex items-center justify-center rounded-lg border-2 border-dashed transition-all cursor-pointer ${
                                                      dragOver[indexSector]
                                                        ? 'border-[#0085BD] bg-[#0085BD]/20 scale-105'
                                                        : uploadingImages[indexSector]
                                                        ? 'border-yellow-400 bg-yellow-50'
                                                        : 'border-[#0085BD]/50 bg-[#0085BD]/10 hover:bg-[#0085BD]/20 hover:border-[#0085BD]'
                                                    }`}
                                                    onDragOver={(e) => handleDragOver(e, indexSector)}
                                                    onDragLeave={(e) => handleDragLeave(e, indexSector)}
                                                    onDrop={(e) => handleDrop(e, indexSector, setFieldValue, values.sectors[indexSector].images || [], values.sectors[indexSector].imagesPreview || [])}
                                                  >
                                                    <input
                                                      type="file"
                                                      id={`sectors[${indexSector}].multipleImages`}
                                                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                      multiple
                                                      accept="image/*,.heic,.heif"
                                                      onChange={(e) => {
                                                        if (e.target.files && e.target.files.length > 0) {
                                                          processMultipleImages(
                                                            e.target.files,
                                                            indexSector,
                                                            setFieldValue,
                                                            values.sectors[indexSector].images || [],
                                                            values.sectors[indexSector].imagesPreview || []
                                                          );
                                                          e.target.value = ''; // Reset para permitir selecionar os mesmos arquivos novamente
                                                        }
                                                      }}
                                                      disabled={uploadingImages[indexSector]}
                                                    />

                                                    {uploadingImages[indexSector] ? (
                                                      <div className="flex flex-col items-center gap-1 px-3">
                                                        <div className="w-8 h-8 border-2 border-[#0085BD] border-t-transparent rounded-full animate-spin" />
                                                        <span className="text-xs text-[#0085BD] font-medium whitespace-nowrap">
                                                          {uploadProgress[indexSector]?.current || 0}/{uploadProgress[indexSector]?.total || 0}
                                                        </span>
                                                      </div>
                                                    ) : (
                                                      <div className="flex flex-col items-center gap-1 px-3 pointer-events-none">
                                                        <FiPlus size={20} className="text-[#0085BD]" />
                                                        <span className="text-[10px] text-[#0085BD] font-medium whitespace-nowrap">
                                                          {dragOver[indexSector] ? 'Soltar aqui' : 'Adicionar fotos'}
                                                        </span>
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ),
                          )
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <p className="text-sm">Nenhum setor adicionado</p>
                            <p className="text-xs text-gray-400">Clique no botão abaixo para adicionar um setor</p>
                          </div>
                        )}
                        <div className="mt-4 flex justify-center">
                          <button
                            type="button"
                            onClick={() => {
                              arraySectors.push({
                                sector_id: "",
                                equipments: selectEquipments,
                                observation: "",
                                show: true,
                                images: [{}],
                                imagesPreview: [],
                              });
                            }}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#0085BD] to-[#006a96] text-white rounded-xl hover:from-[#006a96] hover:to-[#005580] transition-all duration-200 shadow-md hover:shadow-lg group"
                          >
                            <FiPlus
                              size={20}
                              className="transition-transform duration-200 group-hover:rotate-90"
                            />
                            <span className="font-medium">
                              Adicionar Setor
                            </span>
                          </button>
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* Seção 3: Observações */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Observações e Pendências
                  </h3>
                </div>

                <div className="p-6 space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">
                      Ficou alguma pendência?
                    </label>
                    <SelectSingle
                      name="pending"
                      defaultValue=""
                      onChange={e => setFieldValue("pending", e.target.value)}
                      error={errors.pending}
                    >
                      <option value="" disabled>
                        Sim ou Não
                      </option>
                      <option value="true">Sim</option>
                      <option value="false">Não</option>
                    </SelectSingle>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">
                      Notas
                    </label>
                    <Field
                      as="textarea"
                      placeholder="Observações gerais"
                      name="observation"
                      className="h-[104px] p-4 w-full rounded-xl text-gray-700 bg-gray-50 border border-gray-200 focus:border-[#0085BD] focus:ring-2 focus:ring-[#0085BD]/20 focus:bg-white resize-none transition-all duration-200 scrollbar-thumb-[#0085BD] scrollbar-track-transparent scrollbar-thin"
                    />
                  </div>

                  {/* Campo de Emergência - Múltiplas Observações */}
                  <div className="p-4 rounded-xl border-2 border-dashed border-red-300 bg-gradient-to-br from-red-50 to-orange-50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-red-100 rounded-lg">
                        <FiAlertOctagon size={16} className="text-red-600" />
                      </div>
                      <span className="text-sm font-semibold text-red-600">
                        Observações de Emergência (opcional)
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">
                      Adicione observações que requerem atenção urgente. Cada observação marcará o cliente com status de emergência.
                    </p>
                    <FieldArray
                      name="emergency_observations"
                      render={arrayHelpers => (
                        <div className="space-y-3">
                          {values.emergency_observations &&
                            values.emergency_observations.length > 0 &&
                            values.emergency_observations.map(
                              (obs, index) => (
                                <div
                                  key={`emergency-obs-${index}`}
                                  className="flex gap-2 items-start"
                                >
                                  <Field
                                    as="textarea"
                                    placeholder={`Observação ${index + 1}...`}
                                    name={`emergency_observations[${index}]`}
                                    className="flex-1 h-[70px] p-3 rounded-xl text-gray-700 bg-white border border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 resize-none text-sm transition-all duration-200"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => arrayHelpers.remove(index)}
                                    className="p-2.5 text-red-500 hover:bg-red-100 rounded-xl transition-colors"
                                    title="Remover observação"
                                  >
                                    <FiTrash size={18} />
                                  </button>
                                </div>
                              ),
                            )}
                          <button
                            type="button"
                            onClick={() => arrayHelpers.push("")}
                            className="flex items-center gap-2 px-4 py-2.5 text-red-600 border-2 border-red-300 rounded-xl hover:bg-red-100 transition-all duration-200 text-sm font-semibold"
                          >
                            <FiPlus size={16} />
                            Adicionar Observação de Emergência
                          </button>
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Seção 4: Responsáveis e Assinaturas */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Responsáveis e Assinaturas
                  </h3>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Técnico */}
                    <div className="space-y-4">
                      <div className="p-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border border-gray-100">
                        <label className="text-sm font-semibold text-gray-700 mb-2 block">
                          Responsável Técnico
                        </label>
                        <Field
                          as="select"
                          name="technician_id"
                          className="h-[52px] px-4 w-full rounded-xl text-gray-700 bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-2 focus:ring-[#0085BD]/20 transition-all duration-200"
                        >
                          <option value="" defaultChecked disabled>
                            Selecione o técnico responsável
                          </option>
                          {technicians &&
                            technicians.map(({ id: technician_id, name }) => (
                              <option key={technician_id} value={technician_id}>
                                {name}
                              </option>
                            ))}
                        </Field>
                        {errors.technician_id && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.technician_id}
                          </p>
                        )}
                      </div>

                      <div className="p-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border border-gray-100">
                        <label className="text-sm font-semibold text-gray-700 mb-3 block">
                          Assinatura do Técnico
                        </label>
                        <div className="space-y-3">
                          <div className="relative">
                            <button
                              type="button"
                              className="w-full h-[120px] border-2 border-dashed border-[#0085BD] rounded-xl flex items-center justify-center bg-white cursor-pointer hover:bg-blue-50 transition-all duration-200 relative group"
                              onClick={() =>
                                openSignatureModal("technician_signature")
                              }
                            >
                              {technicianSignatureData ? (
                                <>
                                  <img
                                    src={technicianSignatureData}
                                    alt="Assinatura do Técnico"
                                    className="max-h-full max-w-full object-contain"
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded-xl flex items-center justify-center">
                                    <span className="bg-[#0085BD] text-white px-3 py-1 rounded-lg text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                      Clique para editar
                                    </span>
                                  </div>
                                </>
                              ) : (
                                <div className="text-center text-[#7FA5B2]">
                                  <svg
                                    className="mx-auto h-8 w-8 mb-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                    />
                                  </svg>
                                  <p className="text-sm">Clique para assinar</p>
                                </div>
                              )}
                            </button>
                          </div>
                          {technicianSignatureData && (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  openSignatureModal("technician_signature")
                                }
                                className="flex-1 px-3 py-2.5 bg-[#0085BD] text-white rounded-xl hover:bg-[#006a99] transition-all duration-200 text-sm font-semibold flex items-center justify-center gap-2"
                              >
                                <FiEdit2 size={16} /> Editar Assinatura
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setConfirmDelete({
                                    show: true,
                                    type: "technician",
                                  })
                                }
                                className="px-3 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all duration-200 text-sm font-semibold flex items-center justify-center gap-2"
                              >
                                <FiTrash2 size={16} /> Remover
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Cliente */}
                    <div className="space-y-4">
                      <div className="p-4 bg-gradient-to-br from-gray-50 to-green-50 rounded-xl border border-gray-100">
                        <label className="text-sm font-semibold text-gray-700 mb-2 block">
                          Responsável do Cliente
                        </label>
                        <div className="flex gap-2">
                          <Field
                            as="select"
                            name="responsible_id"
                            className="h-[52px] px-4 w-full rounded-xl text-gray-700 bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-2 focus:ring-[#0085BD]/20 transition-all duration-200"
                          >
                            <option value="" defaultChecked disabled>
                              Selecione o responsável
                            </option>
                            {responsibles &&
                              responsibles.map(
                                ({
                                  id: responsible_id,
                                  name: responsible_name,
                                }) => (
                                  <option
                                    key={responsible_id}
                                    value={responsible_id}
                                  >
                                    {responsible_name}
                                  </option>
                                ),
                              )}
                          </Field>
                          <AddButton
                            onClick={() => setModalAddResponsible(true)}
                            tooltip="Adicionar novo responsável"
                            size="md"
                          />
                          {modalAddResponsible && (
                            <ModalAddResponsible
                              setModalAddResponsible={setModalAddResponsible}
                              costumer_id={idCostumer}
                            />
                          )}
                        </div>
                        {errors.responsible_id && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.responsible_id}
                          </p>
                        )}
                      </div>

                      <div className="p-4 bg-gradient-to-br from-gray-50 to-green-50 rounded-xl border border-gray-100">
                        <label className="text-sm font-semibold text-gray-700 mb-3 block">
                          Assinatura do Cliente
                        </label>
                        <div className="space-y-3">
                          <div className="relative">
                            <button
                              type="button"
                              className="w-full h-[120px] border-2 border-dashed border-[#0085BD] rounded-xl flex items-center justify-center bg-white cursor-pointer hover:bg-green-50 transition-all duration-200 relative group"
                              onClick={() =>
                                openSignatureModal("responsible_signature")
                              }
                            >
                              {responsibleSignatureData ? (
                                <>
                                  <img
                                    src={responsibleSignatureData}
                                    alt="Assinatura do Responsável"
                                    className="max-h-full max-w-full object-contain"
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded-xl flex items-center justify-center">
                                    <span className="bg-[#0085BD] text-white px-3 py-1 rounded-lg text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                      Clique para editar
                                    </span>
                                  </div>
                                </>
                              ) : (
                                <div className="text-center text-[#7FA5B2]">
                                  <svg
                                    className="mx-auto h-8 w-8 mb-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                    />
                                  </svg>
                                  <p className="text-sm">Clique para assinar</p>
                                </div>
                              )}
                            </button>
                          </div>
                          {responsibleSignatureData && (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  openSignatureModal("responsible_signature")
                                }
                                className="flex-1 px-3 py-2.5 bg-[#0085BD] text-white rounded-xl hover:bg-[#006a99] transition-all duration-200 text-sm font-semibold flex items-center justify-center gap-2"
                              >
                                <FiEdit2 size={16} /> Editar Assinatura
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setConfirmDelete({
                                    show: true,
                                    type: "responsible",
                                  })
                                }
                                className="px-3 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all duration-200 text-sm font-semibold flex items-center justify-center gap-2"
                              >
                                <FiTrash2 size={16} /> Remover
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {modalAddSignature && (
                  <div
                    className={!modalAddSignature ? "hidden" : "relative z-10"}
                    aria-labelledby="modal-title"
                    role="dialog"
                    aria-modal="true"
                  >
                    <ModalAddSignatureLocal
                      type={signatureType}
                      setModalAddSignature={setModalAddSignature}
                      setTechnicianSignature={() => undefined}
                      setResponsibleSignature={() => undefined}
                      onSaveSignature={handleSaveSignatureLocal}
                    />
                  </div>
                )}
              </div>

              {/* Botão de Envio */}
              <div className="pt-4 mt-2 flex flex-col sm:flex-row gap-3">
                {id === undefined && (
                  <div className="flex-1 sm:flex-initial sm:w-48">
                    <FormButton
                      title="Salvar Rascunho"
                      type="button"
                      variant="primary"
                      icon="save"
                      onClick={() => {
                        saveDraft(values, values.sectors);
                        enqueueSnackbar("Rascunho salvo manualmente!", {
                          variant: "success",
                          anchorOrigin: { vertical: "top", horizontal: "right" },
                        });
                      }}
                    />
                  </div>
                )}
                <div className="flex-1">
                  <FormButton
                    title={
                      isSubmitting
                        ? "Processando..."
                        : id
                        ? "Atualizar Relatório"
                        : "Finalizar Relatório"
                    }
                    type="submit"
                    variant="success"
                    icon="check"
                    disabled={loading || isSubmitting}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>

        {/* Modal de Confirmação de Exclusão */}
        <Modal
          isOpen={confirmDelete.show}
          onClose={() => setConfirmDelete({ show: false, type: "" })}
          title="Confirmar Exclusão"
          icon={<FiTrash size={24} />}
          variant="danger"
          footer={
            <>
              <ModalButton
                variant="cancel"
                onClick={() => setConfirmDelete({ show: false, type: "" })}
              >
                Cancelar
              </ModalButton>
              <ModalButton variant="danger" onClick={handleConfirmDelete}>
                Remover
              </ModalButton>
            </>
          }
        >
          <p className="text-gray-600">
            Tem certeza que deseja remover a assinatura do{" "}
            <strong>
              {confirmDelete.type === "technician"
                ? "técnico"
                : "cliente/responsável"}
            </strong>
            ?
          </p>
        </Modal>

        {/* Modal de Preview de Imagem */}
        {imagePreview.show && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-4xl max-h-full">
              {/* Botão Fechar */}
              <button
                type="button"
                onClick={closeImagePreview}
                className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors z-10"
                aria-label="Fechar preview"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Imagem */}
              <button
                type="button"
                className="p-0 border-0 bg-transparent focus:outline-none"
                onClick={closeImagePreview}
                aria-label="Fechar preview da imagem"
              >
                <img
                  src={imagePreview.src}
                  alt={imagePreview.alt}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  style={{ cursor: 'zoom-out' }}
                />
              </button>

              {/* Overlay clicável para fechar */}
              <button
                type="button"
                className="absolute inset-0 -z-10 bg-transparent border-0 focus:outline-none"
                onClick={closeImagePreview}
                aria-label="Fechar preview"
                tabIndex={-1}
              />

              {/* Legenda */}
              {imagePreview.alt && (
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-center py-2 px-4 rounded-b-lg">
                  {imagePreview.alt}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Botão Flutuante de Emergência */}
        {hasEmergency && (
          <div ref={emergencyPanelRef} className="fixed bottom-6 right-6 z-50">
            {/* Painel Expandido */}
            {showEmergencyPanel && (
              <div className="absolute bottom-16 right-0 w-80 max-h-96 bg-white rounded-lg shadow-xl border border-red-200 overflow-hidden">
                <div className="bg-red-500 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FiAlertOctagon className="text-white" size={20} />
                    <span className="text-white font-medium">
                      Observações Emergenciais ({emergencyObservations.length})
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowEmergencyPanel(false)}
                    className="text-white hover:text-red-200"
                  >
                    <FiX size={20} />
                  </button>
                </div>

                <div className="overflow-y-auto max-h-72 p-3 space-y-2">
                  {emergencyObservations.map((obs, index) => (
                    <div
                      key={obs.id}
                      className={`p-3 rounded-lg border ${
                        selectedObservationsToClose.has(obs.id)
                          ? "bg-green-50 border-green-300"
                          : "bg-red-50 border-red-200"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          onClick={() => toggleObservationSelect(obs.id)}
                          className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                            selectedObservationsToClose.has(obs.id)
                              ? "border-green-500 bg-green-500"
                              : "border-gray-300 hover:border-green-500"
                          }`}
                        >
                          {selectedObservationsToClose.has(obs.id) && (
                            <FiCheck className="text-white" size={12} />
                          )}
                        </button>

                        <div className="flex-1 min-w-0">
                          <button
                            type="button"
                            onClick={() => toggleObservationExpand(obs.id)}
                            className="w-full text-left"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-red-600">
                                #{index + 1}
                              </span>
                              {expandedObservations.has(obs.id) ? (
                                <FiChevronUp size={14} className="text-gray-400" />
                              ) : (
                                <FiChevronDown size={14} className="text-gray-400" />
                              )}
                            </div>
                            <p
                              className={`text-sm text-gray-700 mt-1 ${
                                expandedObservations.has(obs.id)
                                  ? ""
                                  : "line-clamp-2"
                              }`}
                            >
                              {obs.text}
                            </p>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {selectedObservationsToClose.size > 0 && (
                  <div className="border-t border-gray-200 p-3">
                    <p className="text-xs text-gray-500 mb-2">
                      {selectedObservationsToClose.size} observação(ões) marcada(s) para fechar
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Botão Flutuante */}
            <button
              type="button"
              onClick={() => setShowEmergencyPanel(!showEmergencyPanel)}
              className="w-14 h-14 bg-red-500 rounded-full shadow-lg flex items-center justify-center hover:bg-red-600 transition-all hover:scale-110 animate-pulse"
            >
              <FiAlertOctagon className="text-white" size={28} />
              <span className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center text-xs font-bold text-red-800">
                {emergencyObservations.length}
              </span>
            </button>
          </div>
        )}

        {/* Modal de Confirmação de Observações ao Salvar */}
        {showEmergencyConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full overflow-hidden">
              <div className="bg-red-500 px-6 py-4 flex items-center gap-3">
                <FiAlertOctagon className="text-white" size={24} />
                <h3 className="text-lg font-semibold text-white">
                  Observações Emergenciais
                </h3>
              </div>

              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  Este cliente possui observações emergenciais. Selecione as que
                  foram resolvidas nesta visita:
                </p>

                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {emergencyObservations.map((obs, index) => (
                    // eslint-disable-next-line jsx-a11y/label-has-associated-control
                    <label
                      key={obs.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedObservationsToClose.has(obs.id)
                          ? "bg-green-50 border-green-300"
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedObservationsToClose.has(obs.id)}
                        onChange={() => toggleObservationSelect(obs.id)}
                        className="mt-1 w-4 h-4 text-green-500 rounded focus:ring-green-500"
                      />
                      <div className="flex-1">
                        <span className="text-xs font-semibold text-red-600">
                          Observação #{index + 1}
                        </span>
                        <p className="text-sm text-gray-700 mt-1">{obs.text}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmergencyConfirmModal(false);
                    setPendingFormData(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    setShowEmergencyConfirmModal(false);

                    // Processar o submit com os dados pendentes PRIMEIRO
                    // (isso cria a nova observação se houver)
                    if (pendingFormData) {
                      // Filtrar observações válidas (não vazias)
                      const validObservations =
                        pendingFormData.emergency_observations?.filter(
                          (obs) => obs.trim(),
                        ) || [];

                      // Se há observações para fechar E novas observações para criar,
                      // primeiro criamos as novas, depois fechamos as antigas
                      if (
                        selectedObservationsToClose.size > 0 &&
                        validObservations.length > 0
                      ) {
                        // Criar as novas observações primeiro
                        try {
                          await Promise.all(
                            validObservations.map((obsText) =>
                              api.post("/costumer-observations", {
                                costumer_id: pendingFormData.costumer_id,
                                text: obsText.trim(),
                              }),
                            ),
                          );
                        } catch (obsError) {
                          // eslint-disable-next-line no-console
                          console.error(
                            "Erro ao criar observações de emergência:",
                            obsError,
                          );
                        }

                        // Agora fechar as observações selecionadas
                        await closeSelectedObservations();

                        // Processar submit sem criar observação novamente
                        await processSubmit({
                          costumer_id: pendingFormData.costumer_id,
                          begin: pendingFormData.begin,
                          end: pendingFormData.end,
                          observation: pendingFormData.observation,
                          pending: pendingFormData.pending,
                          sectors: pendingFormData.sectors,
                          services: pendingFormData.services,
                          responsible_id: pendingFormData.responsible_id,
                          technician_id: pendingFormData.technician_id,
                          emergency_observations: [], // Já foram criadas acima
                        });
                      } else {
                        // Fechar observações selecionadas se houver
                        if (selectedObservationsToClose.size > 0) {
                          await closeSelectedObservations();
                        }

                        // Processar o submit normalmente
                        await processSubmit({
                          costumer_id: pendingFormData.costumer_id,
                          begin: pendingFormData.begin,
                          end: pendingFormData.end,
                          observation: pendingFormData.observation,
                          pending: pendingFormData.pending,
                          sectors: pendingFormData.sectors,
                          services: pendingFormData.services,
                          responsible_id: pendingFormData.responsible_id,
                          technician_id: pendingFormData.technician_id,
                          emergency_observations:
                            pendingFormData.emergency_observations,
                        });
                      }
                      setPendingFormData(null);
                    }
                  }}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                >
                  <FiCheck size={16} />
                  Confirmar e Salvar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
